import { CDTType, InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import {
  GetCoverageStatusCountsByOrgRequest,
  VerificationCountsByLocationIdsRequest,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { SchemaInsuranceVerificationAdapterServiceV2 } from '@frontend/schema';
import { useGetInsuranceAlertsByOrgIdOptions } from './types';

interface GetCDTCodesForLocationParams {
  locationId: string;
  cdtType: CDTType;
  enableFetchingLocationFavoritesAndRequestsCDTCodes: boolean;
  isEnhancedInsuranceVerificationEnabled: boolean;
}

const queryKeys = {
  base: ['insurance'] as const,
  insuranceAlertsCount: (request: GetCoverageStatusCountsByOrgRequest) => [
    ...queryKeys.base,
    'insurance-alerts-count',
    request,
  ],
  practiceType: (locationId: string) => [...queryKeys.base, 'practice-type-list', locationId],
  nonDentalEligibilityReport: (locationId: string, requestId: string) => [
    ...queryKeys.base,
    'eligibility-report',
    locationId,
    requestId,
  ],
  iframeConnectionUrl: (locationId: string) => [...queryKeys.base, 'iframe-connection-url', locationId],
  getCheckInsuranceCount: (personIds?: string[]) => [...queryKeys.base, 'insurance-alerts-count', ...(personIds || [])],
  getListAllCDTCodes: () => [...queryKeys.base, 'cdt-code-list'],
  getCDTCodesForLocation: (locationId: string, cdtType: CDTType) => [
    ...queryKeys.base,
    'cdt-codes-for-location',
    locationId,
    cdtType,
  ],
  getInsuranceEligibilityReportEncodedHtml: (locationId: string, personId: string, insuranceType: string) => [
    ...queryKeys.base,
    'eligibility-report-encoded-html',
    locationId,
    personId,
    insuranceType,
  ],
};

const LOCATION_ID_HEADER = 'Location-Id';
const getLocationIdHeader = (locationId?: string) => ({
  headers: locationId ? { [LOCATION_ID_HEADER]: locationId } : undefined,
});

export const useGetInsuranceAlertsByOrgId = (
  request: GetCoverageStatusCountsByOrgRequest,
  options: UseQueryOptions<useGetInsuranceAlertsByOrgIdOptions>
) => {
  const { orgId, locationIds, startDate, endDate } = request;
  return useQuery({
    queryKey: queryKeys.insuranceAlertsCount({ orgId, locationIds, startDate, endDate }),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetCoverageStatusCountsByOrg({
        orgId,
        locationIds,
        startDate,
        endDate,
      }),
    enabled: !!options.enabled,
  });
};

export const useGetPracticeTypeList = (locationId: string, enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.practiceType(locationId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetPVerifyPracticeType({}, getLocationIdHeader(locationId)),
    enabled: !!locationId && enabled,
    retry: false,
  });
};

export const useGetNonDentalInsuranceEligibilityReport = (
  locationId: string,
  requestId: string,
  options: UseQueryOptions<useGetInsuranceAlertsByOrgIdOptions>
) => {
  return useQuery({
    queryKey: queryKeys.nonDentalEligibilityReport(locationId, requestId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetInsuranceEligibilityReport(
        {
          requestId,
        },
        getLocationIdHeader(locationId)
      ),
    enabled: !!(locationId && requestId && options.enabled),
    retry: false,
  });
};

export const useInvalidateQueries = () => {
  const queryClient = useQueryClient();

  return {
    invalidateGetPracticeTypeList: (locationId: string) => {
      queryClient.invalidateQueries(queryKeys.practiceType(locationId));
    },
    invalidateCheckInsuranceCountQuery: () => {
      queryClient.invalidateQueries(queryKeys.getCheckInsuranceCount());
    },
    invalidateGetCDTCodesForLocation: (locationId: string, cdtType: CDTType) => {
      queryClient.invalidateQueries(queryKeys.getCDTCodesForLocation(locationId, cdtType));
    },
  };
};

export const useGetIframeConnectionUrl = (originDomain: string, locationId: string) => {
  return useQuery({
    queryKey: queryKeys.iframeConnectionUrl(locationId),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetIFrameConnectionURL(
        {
          originDomain,
        },
        getLocationIdHeader(locationId)
      ),
    enabled: !!(originDomain && locationId),
    refetchOnWindowFocus: true,
    retry: false,
  });
};

export const useGetCheckInsuranceCount = (req: VerificationCountsByLocationIdsRequest) => {
  const personIds = req?.locationPersonIds?.flatMap(({ personIds }) => personIds) || [];
  return useQuery({
    queryKey: queryKeys.getCheckInsuranceCount(personIds),
    queryFn: () => SchemaInsuranceVerificationAdapterServiceV2.GetVerificationCountsByLocationIds(req),
    enabled: req?.locationPersonIds?.length > 0 && personIds?.length > 0,
    cacheTime: 2 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
  });
};

export const useGetListAllCDTCodes = (locationIds: string[], isEnhancedInsuranceVerificationEnabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.getListAllCDTCodes(),
    queryFn: () => SchemaInsuranceVerificationAdapterServiceV2.ListAllCDTCodes({}),
    enabled: locationIds.length > 0 && isEnhancedInsuranceVerificationEnabled,
    retry: false,
  });
};

export const useGetCDTCodesForLocation = ({
  locationId,
  cdtType,
  enableFetchingLocationFavoritesAndRequestsCDTCodes,
  isEnhancedInsuranceVerificationEnabled,
}: GetCDTCodesForLocationParams) => {
  return useQuery({
    queryKey: queryKeys.getCDTCodesForLocation(locationId, cdtType),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetCDTCodesForLocation({ cdtType }, getLocationIdHeader(locationId)),
    enabled: isEnhancedInsuranceVerificationEnabled && enableFetchingLocationFavoritesAndRequestsCDTCodes,
    retry: false,
  });
};

export const useGetInsuranceEligibilityReportEncodedHtml = (
  locationId: string,
  personId: string,
  insuranceType = InsuranceType.INSURANCE_TYPE_PRIMARY
) => {
  return useQuery({
    queryKey: queryKeys.getInsuranceEligibilityReportEncodedHtml(locationId, personId, insuranceType),
    queryFn: () =>
      SchemaInsuranceVerificationAdapterServiceV2.GetInsuranceEligibilityReportEncodedHTML({
        personId,
        locationId,
        insuranceType,
      }),
    enabled: !!(locationId && personId && insuranceType),
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
};
